import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, message } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import axios from 'axios';
import PropostaService from '../../../services/http/services/Propostas/PropostaService';
import ConfigPlanoPropostaService from '../../../services/http/services/Propostas/ConfigPlanoPropostaService';
import ConfiguracaoGeralPropostaService from '../../../services/http/services/Propostas/ConfiguracaoGeralPropostaService';
import { IConfigPlano, IConfiguracaoGeralProposta, IProposta, ISlidePropostaData } from '../interface';
import { getUsuario } from '../../../services/token/usuario';
import { urlBuscaCidades, urlUsersStatusAtivos } from '../../../services/request/urls';
import { getToken } from '../../../services/token';
import { notification } from 'antd';
import { EmitirErro } from '../../../services/EmitirErro';

import { gerarPdfPropostaDireto } from '../utils/gerarPdfProposta';

export interface ICidadeBuscaItem {
    id: number;
    nome: string;
    uf?: string;
    estado?: {
        id: number;
        nome: string;
        uf?: string;
        sigla?: string;
    };
    microrregiao?: {
        mesorregiao?: {
            estado?: {
                id: number;
                sigla: string;
                nome: string;
                uf?: string;
            };
        };
    };
}

export interface IUsuarioItem {
    id: number;
    nome: string;
    email?: string | null;
    celular?: string | null;
    cargo?: { id?: number; nome?: string } | string | null;
    ativo?: boolean;
    vendedor?: boolean;
}

const TEXTO_PADRAO_PAGAMENTO: Record<string, string> = {
    parcela_unica: 'No prazo máximo de 07 (sete) dias após a emissão da Nota Fiscal em parcela única.',
    parcelado: 'Parcelado em parcelas mensais.',
};

export const TEXTO_PADRAO_DETALHAMENTO_HORAS =
    'Horas de desenvolvimento serão utilizadas apenas caso necessário, mediante solicitação e somente após aprovação de orçamento pelo contratante.';

const formatarMoedaLocal = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
};

export const ESTADOS_BRASIL = [
    { sigla: 'AC', nome: 'Acre' },
    { sigla: 'AL', nome: 'Alagoas' },
    { sigla: 'AP', nome: 'Amapá' },
    { sigla: 'AM', nome: 'Amazonas' },
    { sigla: 'BA', nome: 'Bahia' },
    { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' },
    { sigla: 'ES', nome: 'Espírito Santo' },
    { sigla: 'GO', nome: 'Goiás' },
    { sigla: 'MA', nome: 'Maranhão' },
    { sigla: 'MT', nome: 'Mato Grosso' },
    { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' },
    { sigla: 'PA', nome: 'Pará' },
    { sigla: 'PB', nome: 'Paraíba' },
    { sigla: 'PR', nome: 'Paraná' },
    { sigla: 'PE', nome: 'Pernambuco' },
    { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' },
    { sigla: 'RN', nome: 'Rio Grande do Norte' },
    { sigla: 'RS', nome: 'Rio Grande do Sul' },
    { sigla: 'RO', nome: 'Rondônia' },
    { sigla: 'RR', nome: 'Roraima' },
    { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' },
    { sigla: 'SE', nome: 'Sergipe' },
    { sigla: 'TO', nome: 'Tocantins' },
];

export const getCidadeUf = (c: ICidadeBuscaItem | any): string => {
    return (
        c?.uf ||
        c?.estado?.uf ||
        c?.estado?.sigla ||
        c?.microrregiao?.mesorregiao?.estado?.sigla ||
        c?.microrregiao?.mesorregiao?.estado?.uf ||
        ''
    ).toUpperCase();
};

export const useCadastrarProposta = () => {
    const history = useHistory();
    const { idParam } = useParams<{ idParam?: string }>();
    const isEdit = Boolean(idParam);
    const loggedUser = useMemo(() => getUsuario(), []);
    const [form] = Form.useForm();
    const [configPlanos, setConfigPlanos] = useState<IConfigPlano[]>([]);
    const configPlanoId = Form.useWatch('config_plano_proposta_comercial_id', form);
    const [configGeral, setConfigGeral] = useState<IConfiguracaoGeralProposta | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingInicial, setLoadingInicial] = useState<boolean>(true);
    const [loadingPlanos, setLoadingPlanos] = useState<boolean>(false);
    const [loadingConfigGeral, setLoadingConfigGeral] = useState<boolean>(false);
    const [propostaOriginal, setPropostaOriginal] = useState<IProposta | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [previewVisible, setPreviewVisible] = useState<boolean>(false);
    const [usuarios, setUsuarios] = useState<IUsuarioItem[]>([]);
    const [loadingUsuarios, setLoadingUsuarios] = useState<boolean>(false);
    const [cidades, setCidades] = useState<ICidadeBuscaItem[]>([]);
    const [loadingCidades, setLoadingCidades] = useState<boolean>(false);
    const [selectedUf, setSelectedUf] = useState<string>('');
    const [desabilitarSelecaoMunicipio, setDesabilitarSelecaoMunicipio] = useState<boolean>(false);
    const [propostaSalva, setPropostaSalva] = useState<boolean>(false);
    const [propostaParaDownload, setPropostaParaDownload] = useState<ISlidePropostaData | null>(null);
    const [quantidadeLicencas, setQuantidadeLicencas] = useState<number>(1);
    const [quantidadeHoras, setQuantidadeHoras] = useState<number>(0);
    const [valorUnitarioCustom, setValorUnitarioCustom] = useState<number | null>(null);
    const [detalhamentoCustom, setDetalhamentoCustom] = useState<string | null>(null);
    const [textoObjetoCustom, setTextoObjetoCustom] = useState<string | null>(null);
    const [descricaoPlanoCustom, setDescricaoPlanoCustom] = useState<string | null>(null);
    const [textoDispositivoCustom, setTextoDispositivoCustom] = useState<string | null>(null);
    const [possuiTelaCustom, setPossuiTelaCustom] = useState<boolean | null>(null);
    const [tipoTelaCustom, setTipoTelaCustom] = useState<string | null>(null);
    const [houveMudancaPlano, setHouveMudancaPlano] = useState<boolean>(false);
    const [mudancasPlano, setMudancasPlano] = useState<string[]>([]);
    const [alertaMudancaFechado, setAlertaMudancaFechado] = useState<boolean>(false);

    const selectedConfigPlano = useMemo(() => {
        if (!configPlanoId) {
            if (!idParam && configPlanos.length > 0) {
                return configPlanos[0];
            }
            return null;
        }
        return configPlanos.find(c => c.id === configPlanoId) || null;
    }, [configPlanoId, configPlanos, idParam]);

    const carregarUsuarios = useCallback(async () => {
        try {
            setLoadingUsuarios(true);
            const token = getToken();
            const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(urlUsersStatusAtivos, { headers });
            const lista = Array.isArray(response.data) ? response.data : [];
            setUsuarios(lista);
            return lista;
        } catch (error) {
            console.error('Erro ao carregar lista de usuários:', error);
            return [];
        } finally {
            setLoadingUsuarios(false);
        }
    }, []);

    const carregarCidades = useCallback(async () => {
        try {
            setLoadingCidades(true);
            const token = getToken();
            const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(urlBuscaCidades, { headers });
            const lista = Array.isArray(response.data) ? response.data : [];
            lista.sort((a: ICidadeBuscaItem, b: ICidadeBuscaItem) => a.nome.localeCompare(b.nome));
            setCidades(lista);
            return lista;
        } catch (error) {
            console.error('Erro ao carregar cidades:', error);
            return [];
        } finally {
            setLoadingCidades(false);
        }
    }, []);




    const carregarConfigPlanos = useCallback(async () => {
        try {
            setLoadingPlanos(true);
            const data = await ConfigPlanoPropostaService.getAll();
            setConfigPlanos(data);
            if (!idParam && data.length > 0) {
                const planoPadrao = data.find(c => c.is_principal) || data[0];
                form.setFieldsValue({
                    config_plano_proposta_comercial_id: planoPadrao.id
                });
            }
            return data;
        } catch (error) {
            EmitirErro('Erro ao carregar configurações de planos');
            console.error(error);
            return [];
        } finally {
            setLoadingPlanos(false);
        }
    }, [form, idParam]);

    const carregarDadosProposta = useCallback(async (id: string | number, listaCidades: ICidadeBuscaItem[] = []) => {
        setLoading(true);
        try {
            const proposta: IProposta = await PropostaService.getPropostaById(id);
            setPropostaOriginal(proposta);

            if (proposta.gerada === true) {
                message.warning("Esta proposta já foi gerada e não pode ser editada. Para fazer alterações, duplique a proposta.");
                history.push('/propostas-comerciais');
                return;
            }

            setQuantidadeLicencas(proposta.quantidade_licencas || 1);
            setQuantidadeHoras(proposta.quantidade_horas || 0);

            if (proposta.cidade_uf) {
                setSelectedUf(proposta.cidade_uf.toUpperCase());
                setDesabilitarSelecaoMunicipio(false);
            } else {
                setDesabilitarSelecaoMunicipio(true);
            }

            const vendedorPadrao = proposta.gerada === false
                ? (loggedUser?.id || proposta.vendedor_id || proposta.vendedor?.id || proposta.gerado_por_id)
                : (proposta.vendedor_id || proposta.vendedor?.id || proposta.gerado_por_id || loggedUser?.id);

            const baseCidades = listaCidades.length > 0 ? listaCidades : cidades;
            const cidadeEncontrada = baseCidades.find(c =>
                c.nome.toLowerCase() === (proposta.cidade_nome || '').toLowerCase() &&
                (!proposta.cidade_uf || getCidadeUf(c) === (proposta.cidade_uf || '').toUpperCase())
            );

            const cidadeNomeValida = proposta.cidade_nome === 'PROPOSTA COMERCIAL AO MUNICÍPIO DE'
                ? undefined
                : proposta.cidade_nome;

            if (proposta.valor_unitario_plano !== undefined && proposta.valor_unitario_plano !== null) {
                setValorUnitarioCustom(Number(proposta.valor_unitario_plano));
            }
            if (proposta.detalhamento) {
                setDetalhamentoCustom(proposta.detalhamento);
            }
            if (proposta.texto_objeto) {
                setTextoObjetoCustom(proposta.texto_objeto);
            }
            if (proposta.descricao_plano) {
                setDescricaoPlanoCustom(proposta.descricao_plano);
            }
            if (proposta.texto_dispositivo_padrao) {
                setTextoDispositivoCustom(proposta.texto_dispositivo_padrao);
            }
            if (proposta.possui_tela !== undefined && proposta.possui_tela !== null) {
                setPossuiTelaCustom(Boolean(proposta.possui_tela));
            }
            if (proposta.tipo_tela) {
                setTipoTelaCustom(proposta.tipo_tela);
            }

            setHouveMudancaPlano(Boolean(proposta.houve_mudanca_plano));
            setMudancasPlano(proposta.mudancas_plano || []);
            setAlertaMudancaFechado(false);

            form.setFieldsValue({
                cidade_id: cidadeEncontrada ? cidadeEncontrada.id : undefined,
                titulo_cabecalho: proposta.titulo_cabecalho || 'PROPOSTA COMERCIAL AO MUNICÍPIO DE',
                vendedor_id: vendedorPadrao,
                contato_principal_id: proposta.contato_principal_id || proposta.contato_principal?.id || configGeral?.contato_padrao_id,
                cidade_nome: cidadeNomeValida,
                cidade_uf: proposta.cidade_uf,
                config_plano_proposta_comercial_id: proposta.config_plano_id,
                quantidade_licencas: proposta.quantidade_licencas || 1,
                quantidade_horas: proposta.quantidade_horas || 0,
                condicao_pagamento: proposta.condicao_pagamento || 'parcela_unica',
                numero_parcelas: proposta.numero_parcelas || 12,
                forma_pagamento_descricao: proposta.forma_pagamento_descricao,
                detalhamento_horas: proposta.detalhamento_horas || (proposta.quantidade_horas ? TEXTO_PADRAO_DETALHAMENTO_HORAS : undefined),
                enviado_para: proposta.enviado_para
            });
            setPropostaSalva(proposta.gerada !== false);
        } catch (error) {
            EmitirErro('Erro ao carregar dados da proposta');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [form, loggedUser?.id, configGeral?.contato_padrao_id, cidades, history]);

    const carregarConfigGeral = useCallback(async () => {
        try {
            setLoadingConfigGeral(true);
            const data = await ConfiguracaoGeralPropostaService.get();
            setConfigGeral(data);
            if (!idParam && data?.contato_padrao_id) {
                const currentContatoPrincipal = form.getFieldValue('contato_principal_id');
                if (!currentContatoPrincipal) {
                    form.setFieldsValue({
                        contato_principal_id: data.contato_padrao_id
                    });
                }
            }
            return data;
        } catch (error) {
            // mantém fallback
            return null;
        } finally {
            setLoadingConfigGeral(false);
        }
    }, [idParam, form]);

    useEffect(() => {
        let isMounted = true;

        const carregarTudo = async () => {
            setLoadingInicial(true);
            try {
                const [listaUsers, listaCidades] = await Promise.all([
                    carregarUsuarios(),
                    carregarCidades(),
                    carregarConfigPlanos(),
                    carregarConfigGeral()
                ]);

                if (!isMounted) return;

                if (idParam) {
                    await carregarDadosProposta(idParam, listaCidades);
                } else {
                    form.setFieldsValue({
                        titulo_cabecalho: 'PROPOSTA COMERCIAL AO MUNICÍPIO DE',
                        vendedor_id: loggedUser?.id
                    });
                }
            } catch (err) {
                EmitirErro(`Erro no carregamento inicial da proposta: ${err}`);
                console.error('Erro no carregamento inicial da proposta:', err);
            } finally {
                if (isMounted) {
                    setLoadingInicial(false);
                }
            }
        };

        carregarTudo();

        return () => {
            isMounted = false;
        };
    }, [idParam]);

    const handlePlanoChange = (configId: number) => {
        form.setFieldsValue({ config_plano_proposta_comercial_id: configId });
        setValorUnitarioCustom(null);
        setDetalhamentoCustom(null);
        setTextoObjetoCustom(null);
        setDescricaoPlanoCustom(null);
        setTextoDispositivoCustom(null);
        setPossuiTelaCustom(null);
        setTipoTelaCustom(null);
        setHouveMudancaPlano(false);
        setMudancasPlano([]);
    };

    const handleAtualizarComPlanoVigente = () => {
        if (!selectedConfigPlano) return;
        const valorPlano = Number(selectedConfigPlano.plano?.valor || 0);
        setValorUnitarioCustom(valorPlano);
        setDetalhamentoCustom(selectedConfigPlano.detalhamento_padrao || '');
        setTextoObjetoCustom(selectedConfigPlano.texto_objeto_padrao || '');
        setDescricaoPlanoCustom(selectedConfigPlano.descricao_plano || '');
        setTextoDispositivoCustom(selectedConfigPlano.texto_dispositivo_padrao || '');
        setPossuiTelaCustom(Boolean(selectedConfigPlano.possui_tela));
        setTipoTelaCustom(selectedConfigPlano.tipo_tela || (selectedConfigPlano.possui_tela ? 'tela' : null));
        
        if (!selectedConfigPlano.contratacao_horas) {
            setQuantidadeHoras(0);
            form.setFieldsValue({ quantidade_horas: 0 });
        }

        setHouveMudancaPlano(false);
        setMudancasPlano([]);
        setAlertaMudancaFechado(true);
        notification.success({
            message: "Plano Atualizado!",
            description: "Valores, detalhamento e regras da proposta foram sincronizados com as configurações vigentes do plano."
        });
    };

    const handleDescartarAvisoMudanca = () => {
        setAlertaMudancaFechado(true);
    };

    const handleToggleDesabilitarMunicipio = (checked: boolean) => {
        setDesabilitarSelecaoMunicipio(checked);
        setSelectedUf('');
        const currentTitulo = form.getFieldValue('titulo_cabecalho');
        form.resetFields(['cidade_id', 'cidade_nome', 'cidade_uf']);
        form.setFieldsValue({
            cidade_id: undefined,
            cidade_uf: checked ? '' : undefined,
            cidade_nome: '',
            titulo_cabecalho: currentTitulo || 'PROPOSTA COMERCIAL AO MUNICÍPIO DE'
        });
    };

    const handleCidadeSelect = (cidadeId?: number) => {
        if (!cidadeId) {
            form.resetFields(['cidade_id', 'cidade_nome', 'cidade_uf']);
            form.setFieldsValue({
                cidade_id: undefined,
                cidade_nome: '',
                cidade_uf: ''
            });
            setSelectedUf('');
            return;
        }
        const selected = cidades.find(c => c.id === cidadeId);
        if (selected) {
            const uf = getCidadeUf(selected);
            form.setFieldsValue({
                cidade_id: selected.id,
                cidade_nome: selected.nome,
                cidade_uf: uf
            });
            setSelectedUf(uf);
        }
    };

    const handleUfChange = (uf?: string) => {
        const ufUpper = (uf || '').toUpperCase();
        setSelectedUf(ufUpper);
        form.resetFields(['cidade_id', 'cidade_nome', 'cidade_uf']);
        form.setFieldsValue({
            cidade_uf: ufUpper || undefined,
            cidade_id: undefined,
            cidade_nome: ''
        });
    };

    // Cidades filtradas pela UF selecionada
    const cidadesFiltradas = useMemo(() => {
        if (!selectedUf) return [];
        return cidades
            .filter(c => getCidadeUf(c) === selectedUf.toUpperCase())
            .sort((a, b) => a.nome.localeCompare(b.nome));
    }, [cidades, selectedUf]);

    const valorUnitarioPlano = valorUnitarioCustom !== null
        ? valorUnitarioCustom
        : Number(selectedConfigPlano?.plano?.valor || 0);
    const valorTotalLicencas = quantidadeLicencas * valorUnitarioPlano;

    const contratacaoHoras = Boolean(selectedConfigPlano?.contratacao_horas);
    const valorUnitarioHora = contratacaoHoras 
        ? Number(configGeral?.valor_hora_padrao ?? 150)
        : 0;
    const valorTotalHoras = contratacaoHoras ? quantidadeHoras * valorUnitarioHora : 0;

    const valorTotalGeral = valorTotalLicencas + valorTotalHoras;

    const condicaoPagamento = Form.useWatch('condicao_pagamento', form) || 'parcela_unica';
    const numeroParcelas = Form.useWatch('numero_parcelas', form) || 12;
    // A parcela mensal é calculada apenas sobre o valor das licenças (as horas sob demanda são faturadas à parte)
    const valorParcela = valorTotalLicencas > 0 ? valorTotalLicencas / (Number(numeroParcelas) || 12) : 0;

    useEffect(() => {
        if (!condicaoPagamento) return;

        if (condicaoPagamento === 'parcelado' && !form.getFieldValue('numero_parcelas')) {
            form.setFieldValue('numero_parcelas', 12);
        }

        const valorAtual = (form.getFieldValue('forma_pagamento_descricao') || '').trim();
        const textosPadrao = Object.values(TEXTO_PADRAO_PAGAMENTO);

        const isTextoPadrao = !valorAtual ||
            textosPadrao.includes(valorAtual) ||
            /^Parcelado em \d+x de .+ mensais\.?$/i.test(valorAtual) ||
            /^Licenciamento parcelado em \d+x de .+ mensais/i.test(valorAtual) ||
            /Horas técnicas faturadas sob demanda/i.test(valorAtual) ||
            /Licenciamento em parcela única/i.test(valorAtual);

        if (isTextoPadrao) {
            if (condicaoPagamento === 'parcela_unica') {
                if (contratacaoHoras && quantidadeHoras > 0) {
                    form.setFieldValue(
                        'forma_pagamento_descricao',
                        `Licenciamento em parcela única no prazo de até 07 dias após a emissão da NF. Horas técnicas faturadas sob demanda conforme ordem de serviço e horas executadas.`
                    );
                } else {
                    form.setFieldValue(
                        'forma_pagamento_descricao',
                        TEXTO_PADRAO_PAGAMENTO.parcela_unica
                    );
                }
            } else if (condicaoPagamento === 'parcelado') {
                const parcelas = Number(numeroParcelas) || 12;
                if (contratacaoHoras && quantidadeHoras > 0) {
                    const textoGerado = `Licenciamento parcelado em ${parcelas}x de ${formatarMoedaLocal(valorParcela)} mensais. Horas técnicas faturadas sob demanda conforme ordem de serviço e horas executadas.`;
                    form.setFieldValue('forma_pagamento_descricao', textoGerado);
                } else {
                    const textoGerado = `Parcelado em ${parcelas}x de ${formatarMoedaLocal(valorParcela)} mensais.`;
                    form.setFieldValue('forma_pagamento_descricao', textoGerado);
                }
            }
        }
    }, [condicaoPagamento, numeroParcelas, valorParcela, contratacaoHoras, quantidadeHoras, form]);

    useEffect(() => {
        if (contratacaoHoras && quantidadeHoras > 0) {
            const atual = form.getFieldValue('detalhamento_horas');
            if (!atual || !atual.trim()) {
                form.setFieldValue('detalhamento_horas', TEXTO_PADRAO_DETALHAMENTO_HORAS);
            }
        } else {
            if (form.getFieldValue('detalhamento_horas')) {
                form.setFieldValue('detalhamento_horas', undefined);
            }
        }
    }, [contratacaoHoras, quantidadeHoras, form]);

    const getPreviewData = useCallback((propostaId?: number): ISlidePropostaData => {
        const values = form.getFieldsValue();
        const uf = values.cidade_uf || '';
        const muni = values.cidade_nome || 'Município';

        const getCargoDesc = (cargo: any): string => {
            if (!cargo) return '';
            if (typeof cargo === 'object' && cargo.nome) return String(cargo.nome);
            if (typeof cargo === 'string') return cargo;
            return '';
        };

        const vendedorId = values.vendedor_id || loggedUser?.id;
        const vendedorSelecionado = usuarios.find(u => u.id === vendedorId);

        const vendedorNome = vendedorSelecionado?.nome || loggedUser?.nome || '';
        const vendedorCargo = getCargoDesc(vendedorSelecionado?.cargo)
            || getCargoDesc(loggedUser?.cargo)
            || '';
        const vendedorTelefone = vendedorSelecionado?.celular || loggedUser?.celular || '';
        const vendedorEmail = vendedorSelecionado?.email || loggedUser?.email || '';

        const contatoPrincipalId = values.contato_principal_id || configGeral?.contato_padrao_id;
        const contatoPrincipalSelecionado = usuarios.find(u => u.id === contatoPrincipalId);

        const contatoPrincipalNome = contatoPrincipalSelecionado?.nome || configGeral?.contato_padrao?.nome || '';
        const contatoPrincipalCargo = getCargoDesc(contatoPrincipalSelecionado?.cargo)
            || getCargoDesc(configGeral?.contato_padrao?.cargo)
            || '';
        const contatoPrincipalTelefone = contatoPrincipalSelecionado?.celular || configGeral?.contato_padrao?.celular || '';
        const contatoPrincipalEmail = contatoPrincipalSelecionado?.email || configGeral?.contato_padrao?.email || '';

        const finalId = propostaId !== undefined ? propostaId : (isEdit && idParam ? Number(idParam) : propostaOriginal?.id);

        const detalhamentoFinal = detalhamentoCustom !== null
            ? detalhamentoCustom
            : (selectedConfigPlano?.detalhamento_padrao || propostaOriginal?.detalhamento || '');

        const textoObjetoFinal = textoObjetoCustom !== null
            ? textoObjetoCustom
            : (selectedConfigPlano?.texto_objeto_padrao || propostaOriginal?.texto_objeto || '');

        const descricaoPlanoFinal = descricaoPlanoCustom !== null
            ? descricaoPlanoCustom
            : (selectedConfigPlano?.descricao_plano || propostaOriginal?.descricao_plano || selectedConfigPlano?.plano?.nome || 'Licença Intermediária');

        const textoDispositivoFinal = textoDispositivoCustom !== null
            ? textoDispositivoCustom
            : (selectedConfigPlano?.texto_dispositivo_padrao || propostaOriginal?.texto_dispositivo_padrao || null);

        const possuiTelaFinal = possuiTelaCustom !== null
            ? possuiTelaCustom
            : (selectedConfigPlano?.possui_tela ?? propostaOriginal?.possui_tela ?? true);

        const tipoTelaFinal = tipoTelaCustom !== null
            ? tipoTelaCustom
            : (selectedConfigPlano?.tipo_tela || propostaOriginal?.tipo_tela || (possuiTelaFinal ? 'tela' : null));

        const detalhamentoHorasFinal = (contratacaoHoras && quantidadeHoras > 0)
            ? (values.detalhamento_horas || form.getFieldValue('detalhamento_horas') || propostaOriginal?.detalhamento_horas || TEXTO_PADRAO_DETALHAMENTO_HORAS)
            : null;

        return {
            id: finalId,
            uuid: propostaOriginal?.uuid,
            titulo_cabecalho: values.titulo_cabecalho || 'PROPOSTA COMERCIAL AO MUNICÍPIO DE',
            cidade_nome: muni,
            cidade_uf: uf,
            cidade_completo: uf ? `${muni} - ${uf}` : muni,
            plano_nome: selectedConfigPlano?.plano?.nome || descricaoPlanoFinal,
            descricao_plano: descricaoPlanoFinal,
            texto_objeto: textoObjetoFinal,
            quantidade_licencas: quantidadeLicencas,
            valor_unitario_plano: valorUnitarioPlano,
            valor_total_plano: valorTotalLicencas,
            contratacao_horas: contratacaoHoras,
            quantidade_horas: contratacaoHoras ? quantidadeHoras : null,
            valor_unitario_horas: contratacaoHoras ? valorUnitarioHora : null,
            valor_total_horas: contratacaoHoras ? valorTotalHoras : null,
            valor_total_geral: valorTotalGeral,
            condicao_pagamento: values.condicao_pagamento || 'parcela_unica',
            numero_parcelas: values.condicao_pagamento === 'parcelado' ? Number(values.numero_parcelas || 12) : 1,
            valor_parcela: valorParcela,
            forma_pagamento_descricao: values.forma_pagamento_descricao,
            detalhamento: detalhamentoFinal,
            detalhamento_horas: detalhamentoHorasFinal,
            possui_tela: possuiTelaFinal,
            tipo_tela: tipoTelaFinal,
            texto_dispositivo_padrao: textoDispositivoFinal,
            gerado_em: new Date(),
            gerado_por_nome: loggedUser?.nome || 'Consultor Comercial',
            vendedor_nome: vendedorNome,
            vendedor_cargo: vendedorCargo,
            vendedor_telefone: vendedorTelefone,
            vendedor_email: vendedorEmail,
            contato_principal_nome: contatoPrincipalNome,
            contato_principal_cargo: contatoPrincipalCargo,
            contato_principal_telefone: contatoPrincipalTelefone,
            contato_principal_email: contatoPrincipalEmail,
            municipios_destaque: configGeral?.municipios_destaque,
            gerada: propostaSalva,
        };
    }, [form, loggedUser, usuarios, selectedConfigPlano, configGeral, quantidadeLicencas, valorUnitarioPlano, valorTotalLicencas, contratacaoHoras, quantidadeHoras, valorUnitarioHora, valorTotalHoras, valorTotalGeral, propostaSalva, detalhamentoCustom, textoObjetoCustom, descricaoPlanoCustom, textoDispositivoCustom, possuiTelaCustom, tipoTelaCustom, propostaOriginal]);

    const printRef = useRef<HTMLDivElement>(null);

    const handleOpenPreview = () => {
        setPreviewVisible(true);
    };

    const onFinish = async (values: any) => {
        setSubmitting(true);
        try {
            const detalhamentoFinal = detalhamentoCustom !== null
                ? detalhamentoCustom
                : (selectedConfigPlano?.detalhamento_padrao || propostaOriginal?.detalhamento || null);

            const textoObjetoFinal = textoObjetoCustom !== null
                ? textoObjetoCustom
                : (selectedConfigPlano?.texto_objeto_padrao || propostaOriginal?.texto_objeto || null);

            const descricaoPlanoFinal = descricaoPlanoCustom !== null
                ? descricaoPlanoCustom
                : (selectedConfigPlano?.descricao_plano || propostaOriginal?.descricao_plano || null);

            const textoDispositivoFinal = textoDispositivoCustom !== null
                ? textoDispositivoCustom
                : (selectedConfigPlano?.texto_dispositivo_padrao || propostaOriginal?.texto_dispositivo_padrao || null);

            const possuiTelaFinal = possuiTelaCustom !== null
                ? possuiTelaCustom
                : (selectedConfigPlano?.possui_tela ?? propostaOriginal?.possui_tela ?? null);

            const tipoTelaFinal = tipoTelaCustom !== null
                ? tipoTelaCustom
                : (selectedConfigPlano?.tipo_tela || propostaOriginal?.tipo_tela || null);

            const payload = {
                ...values,
                numero_parcelas: values.condicao_pagamento === 'parcelado' ? Number(values.numero_parcelas || 12) : 1,
                vendedor_id: values.vendedor_id || loggedUser?.id,
                contato_principal_id: values.contato_principal_id || configGeral?.contato_padrao_id || null,
                cidade_uf: desabilitarSelecaoMunicipio ? '' : (values.cidade_uf || ''),
                titulo_cabecalho: values.titulo_cabecalho || 'PROPOSTA COMERCIAL AO MUNICÍPIO DE',
                valor_unitario_plano: valorUnitarioPlano,
                valor_total_licenca: valorTotalLicencas,
                valor_total_hora: contratacaoHoras ? valorTotalHoras : null,
                quantidade_horas: contratacaoHoras ? Number(values.quantidade_horas || 0) : null,
                detalhamento: detalhamentoFinal,
                detalhamento_horas: (contratacaoHoras && quantidadeHoras > 0)
                    ? (values.detalhamento_horas || form.getFieldValue('detalhamento_horas') || TEXTO_PADRAO_DETALHAMENTO_HORAS)
                    : null,
                texto_objeto: textoObjetoFinal,
                descricao_plano: descricaoPlanoFinal,
                texto_dispositivo_padrao: textoDispositivoFinal,
                possui_tela: possuiTelaFinal,
                tipo_tela: tipoTelaFinal
            };

            let novaOuAtualizadaProposta: IProposta;
            if (isEdit && idParam) {
                novaOuAtualizadaProposta = await PropostaService.updateProposta(idParam, { ...payload, id: Number(idParam) });
                notification.success({
                    message: "Sucesso !",
                    description: 'Proposta atualizada com sucesso!'
                });
            } else {
                novaOuAtualizadaProposta = await PropostaService.createProposta(payload);
                notification.success({
                    message: "Sucesso !",
                    description: 'Proposta criada com sucesso!'
                });
            }

            setPropostaSalva(true);

            const finalId = novaOuAtualizadaProposta?.id || (idParam ? Number(idParam) : undefined);
            const slideDataFinal = getPreviewData(finalId);
            setPropostaParaDownload(slideDataFinal);

            // Aguarda o ciclo de renderização do DOM com o ID preenchido
            await new Promise(resolve => setTimeout(resolve, 350));

            if (printRef.current) {
                const muni = (values.cidade_nome || 'Municipio').replace(/\s+/g, '_');
                const uf = values.cidade_uf ? `_${values.cidade_uf.toUpperCase()}` : '';
                const docTitle = `Proposta_Comercial_${muni}${uf}_GovFacil`;

                message.loading({ content: 'Proposta salva! Gerando e baixando PDF...', key: 'pdf_save_download', duration: 0 });
                try {
                    await gerarPdfPropostaDireto({
                        containerElement: printRef.current,
                        documentTitle: docTitle,
                        onProgress: (current, total) => {
                            message.loading({ content: `Gerando PDF da proposta (${current} de ${total})...`, key: 'pdf_save_download', duration: 0 });
                        }
                    });
                    message.success({ content: 'Proposta salva e PDF baixado com sucesso!', key: 'pdf_save_download', duration: 3 });
                } catch (pdfError) {
                    console.error('Erro ao gerar PDF automaticamente:', pdfError);
                    message.warning({ content: 'Proposta salva! Não foi possível gerar o download automático do PDF.', key: 'pdf_save_download', duration: 4 });
                }
            }

            history.push('/propostas-comerciais');
        } catch (error: any) {
            EmitirErro(error);
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleFormChange = () => {
        if (loading) return;
        setPropostaSalva(false);
    };

    const usuariosFiltrados = useMemo(() => {
        return usuarios.filter(u => Boolean(u.vendedor) || u.id === loggedUser?.id);
    }, [usuarios, loggedUser?.id]);

    return {
        form,
        isEdit,
        loading,
        loadingInicial,
        loadingPlanos,
        loadingConfigGeral,
        submitting,
        configPlanos,
        selectedConfigPlano,
        quantidadeLicencas,
        quantidadeHoras,
        valorUnitarioPlano,
        valorTotalLicencas,
        contratacaoHoras,
        valorUnitarioHora,
        valorTotalHoras,
        valorTotalGeral,
        condicaoPagamento,
        numeroParcelas,
        valorParcela,
        previewVisible,
        setPreviewVisible,
        handleOpenPreview,
        getPreviewData,
        setQuantidadeLicencas,
        setQuantidadeHoras,
        handlePlanoChange,
        selectedUf,
        handleUfChange,
        handleCidadeSelect,
        cidadesFiltradas,
        ESTADOS_BRASIL,
        cidades,
        loadingCidades,
        usuarios: usuariosFiltrados,
        loadingUsuarios,
        loggedUser,
        configGeral,
        desabilitarSelecaoMunicipio,
        setDesabilitarSelecaoMunicipio,
        handleToggleDesabilitarMunicipio,
        onFinish,
        handleFormChange,
        houveMudancaPlano,
        mudancasPlano,
        alertaMudancaFechado,
        handleAtualizarComPlanoVigente,
        handleDescartarAvisoMudanca,
        propostaSalva,
        propostaParaDownload,
        history,
        printRef
    };
};
